import { toasts } from '@nexans/react-ui-kit'
import Axios, { AxiosError, AxiosResponse, InternalAxiosRequestConfig } from 'axios'

import { API_PRIVATE_URL, API_PUBLIC_URL, APIM_SUBSCRIPTION_KEY } from '@/config/env'
import { refreshTokenAndUpdateStore } from '@/features/auth'
import { ABILITIES } from '@/lib/casl'
import { useAppStore } from '@/stores/appStore'
import { AvailableCountry } from '@/types/openapi'

import { logger } from './logger'
import { i18next } from './react-i18next'
import { getFileNameFromHeader, getQueryParamsFrom } from './react-query'

declare module 'axios' {
  export interface AxiosRequestConfig {
    disableConnectedAsHeader?: boolean
    _retry?: boolean // Track if request has been retried
  }
}

// Queue to track pending refresh token requests
let isRefreshing = false
let failedQueue: Array<{
  resolve: (token: string | null) => void
  reject: (error: Error) => void
}> = []

const processQueue = (error: Error | null, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error)
    } else {
      prom.resolve(token)
    }
  })

  failedQueue = []
}

const refreshAccessToken = async (): Promise<string | null> => {
  const { refreshToken } = useAppStore.getState()
  if (!refreshToken) {
    throw new Error('No refresh token available')
  }

  try {
    // refreshAndUpdateStore automatically updates the store
    const { accessToken } = await refreshTokenAndUpdateStore(refreshToken)

    logger.info('Token refresh successful via interceptor')
    return accessToken
  } catch (error) {
    logger.error('Token refresh failed via interceptor', error as Error)
    throw error
  }
}

/**
 * Derives the Mobiway country to send via the `Mobiway-Country` header:
 * - `forceCountry` (from the app store), when set, always takes priority.
 * - Otherwise, it's derived from the current site's domain name: `.fr` domains resolve to France,
 *   `.co` domains resolve to Colombia.
 * - Localhost resolves to France so the local application can be used with the configured identity provider.
 * - If neither applies, returns `undefined` and no header is sent.
 */
export function getMobiwayCountry(hostname: string = window.location.hostname): AvailableCountry | undefined {
  const forceCountry = useAppStore.getState().forceCountry
  if (forceCountry) return forceCountry
  if (hostname.endsWith('.co')) return AvailableCountry.CO
  if (hostname.endsWith('.fr')) return AvailableCountry.FR
  if (hostname === 'localhost') return AvailableCountry.FR
  return undefined
}

function commonHeadersInterceptor(config: InternalAxiosRequestConfig) {
  config.headers.set('Timezone', new Intl.DateTimeFormat().resolvedOptions().timeZone)
  const mobiwayCountry = getMobiwayCountry()
  if (mobiwayCountry) {
    config.headers.set('Mobiway-Country', mobiwayCountry)
  }
  if (APIM_SUBSCRIPTION_KEY) {
    config.headers.set('Ocp-Apim-Subscription-Key', APIM_SUBSCRIPTION_KEY)
  }
  return config
}

function authRequestInterceptor(config: InternalAxiosRequestConfig) {
  commonHeadersInterceptor(config)

  // Dynamically set Authorization header with current accessToken
  const accessToken = useAppStore.getState().accessToken
  if (accessToken) {
    config.headers.set('Authorization', `Bearer ${accessToken}`)
  }

  return config
}

function successResponseInterceptor(response: AxiosResponse): AxiosResponse['data'] {
  if (response.config.responseType === 'blob') return response
  return response.data
}

export const axios = Axios.create({
  baseURL: API_PRIVATE_URL,
  withCredentials: true,
})

axios.interceptors.request.use(authRequestInterceptor)
axios.interceptors.response.use(successResponseInterceptor)
axios.interceptors.response.use(
  (response) => {
    return response
  },
  async (error: AxiosError) => {
    const originalRequest = error.config
    const status = error.response?.status

    // Handle 401 Unauthorized - attempt token refresh
    if (status === 401 && originalRequest && !originalRequest._retry) {
      // Skip refresh for /auth/refresh itself to avoid loops. Other endpoints, including /users/me,
      // are retried once thanks to the _retry flag.
      const url = originalRequest.url || ''
      if (url.endsWith('/auth/refresh')) {
        return Promise.reject(error)
      }

      // An unauthenticated request cannot be refreshed and must not trigger a reload loop.
      if (!useAppStore.getState().refreshToken) {
        return Promise.reject(error)
      }

      // Mark request as retried to avoid infinite loops
      originalRequest._retry = true

      if (isRefreshing) {
        // Token refresh already in progress, queue this request
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject })
        })
          .then((token) => {
            if (token && originalRequest.headers) {
              originalRequest.headers.Authorization = `Bearer ${token}`
            }
            return axios(originalRequest)
          })
          .catch((err) => {
            return Promise.reject(err)
          })
      }

      isRefreshing = true
      // Notify store that token refresh is in progress
      useAppStore.getState().actions.setIsRefreshingToken(true)

      try {
        const newAccessToken = await refreshAccessToken()

        if (newAccessToken && originalRequest.headers) {
          originalRequest.headers.Authorization = `Bearer ${newAccessToken}`
        }

        processQueue(null, newAccessToken)
        isRefreshing = false
        useAppStore.getState().actions.setIsRefreshingToken(false)

        // Retry original request with new token
        return axios(originalRequest)
      } catch (refreshError) {
        processQueue(new Error('Token refresh failed'), null)
        isRefreshing = false
        useAppStore.getState().actions.setIsRefreshingToken(false)

        // Token refresh failed - logout user
        logger.warn('Logging out user due to failed token refresh')
        const { clearTokens, clearSessionId, setIsAuthenticated, setAbility } = useAppStore.getState().actions
        clearTokens()
        clearSessionId()
        setIsAuthenticated(false)
        setAbility(ABILITIES.DEFAULT)

        // Redirect to auth page
        window.location.assign(window.location.protocol + '//' + window.location.host)
        return Promise.reject(refreshError)
      }
    }

    // Handle 5xx server errors
    if (status && status >= 500 && status < 600) {
      toasts.error(i18next.t('errors:errors.server_error_generic'), i18next.t('errors:errors.server_error_description'))
      logger.error(`Server Error ${status}`, error, {
        status,
        url: originalRequest?.url,
        method: originalRequest?.method,
      })
    }

    // For other errors, delegate to calling side
    return Promise.reject(error)
  }
)

// Public instance — no authentication, for endpoints accessible without a token
export const publicAxios = Axios.create({
  baseURL: API_PUBLIC_URL,
})

publicAxios.interceptors.request.use(commonHeadersInterceptor)
publicAxios.interceptors.response.use(successResponseInterceptor)
publicAxios.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    const status = error.response?.status
    if (status && status >= 500 && status < 600) {
      toasts.error(i18next.t('errors:errors.server_error_generic'), i18next.t('errors:errors.server_error_description'))
      logger.error(`Server Error ${status}`, error, {
        status,
        url: error.config?.url,
        method: error.config?.method,
      })
    }
    return Promise.reject(error)
  }
)

export const download = async (
  url: string,
  filter: Record<string, unknown> | undefined,
  defaultFileName: string = 'download'
): Promise<void> => {
  const urlWithFilters = url + getQueryParamsFrom(filter)

  return axios.get(urlWithFilters, { responseType: 'blob' }).then((r: AxiosResponse<Blob>) => {
    // Create file
    const url = URL.createObjectURL(r.data)
    // Create "fake" link in DOM
    const link = document.createElement('a')
    const fileName = getFileNameFromHeader(r)
    link.href = url
    link.setAttribute('download', fileName ?? defaultFileName)
    document.body.appendChild(link)
    link.click()
    // Clean
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
  })
}
